import type { AppThunk } from '../../../store';
import { isMultiOrgFeatureEnabled } from '../../../utils/features';
import { selectIsSwitchingTenantSession } from '../../Login/LoginSlice';
import { logoutUser, tryDeletePushToken } from '../../Sidebar/actionCreators';
import { pruneTenantSession, selectActiveTenantId } from '.';
import { resetTenantScopedSlices } from './resetTenantScoped';
import { getPreAddOrgTenantId } from './beginAddOrganization';
import { trySwitchFirstRemainingTenantSession } from './trySwitchFirstRemainingTenantSession';
import { navigateTo } from '../../../routes/navigation';

/** Handles unauthorized responses by pruning one tenant or fully logging out. */
export const handleUnauthorizedResponse = (): AppThunk => async (dispatch, getState) => {
  if (!isMultiOrgFeatureEnabled()) {
    await tryDeletePushToken();
    dispatch(logoutUser());
    return;
  }

  // A 401 during switchActiveOrganization must not re-enter this handler —
  // the switch owns its own rollback and pruning, and two concurrent flows
  // would tear state.
  if (selectIsSwitchingTenantSession(getState())) {
    return;
  }

  // Add-organisation keeps a single cached tenant while ULF overwrites globals.
  // A 401 in that window must not run the "only one session" full wipe — it
  // clears per-tenant storage and logs out, so after the new org login only
  // that tenant remains in the dropdown (cosafe/app multi-org regression).
  if (getPreAddOrgTenantId() != null) {
    dispatch(resetTenantScopedSlices());
    return;
  }

  const activeTenantId = selectActiveTenantId(getState());

  if (activeTenantId != null) {
    dispatch(pruneTenantSession({ tenantId: activeTenantId, alertUser: true }));
  }
  dispatch(resetTenantScopedSlices());

  const hasSwitchedSession = await dispatch(trySwitchFirstRemainingTenantSession());

  if (!hasSwitchedSession) {
    await tryDeletePushToken();
    dispatch(logoutUser());
    return;
  }

  // Redirect to the main screen to ensure that fresh data is loaded and that the correct functionalities are  available to the user (#2349). Note that the isSwitchingTenantSession guard above excludes deliberate tenant session switches from this logic
  navigateTo('/dashboard');
};
